import React, { useState, useMemo } from 'react'
import { useQuery } from 'react-query'
import { Download, BarChart3, TrendingUp, FileText, Users, Banknote, Clock, Target, CheckCircle, ChevronDown, ChevronRight, Calendar, PieChart } from 'lucide-react'
import { projectService } from '@/services/projectService'
import { formatCurrency } from '@/utils/formatters'
import * as XLSX from 'xlsx'
import ProjectGanttReport from './ProjectGanttReport'

const ProjectReports = () => {
  const currentYear = new Date().getFullYear()
  const [activeTab, setActiveTab] = useState('overview')
  const [dateFrom, setDateFrom] = useState(`${currentYear}-01-01`)
  const [dateTo, setDateTo] = useState(`${currentYear}-12-31`)
  const [statusFilter, setStatusFilter] = useState('all')

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'gantt', label: 'Gantt Chart', icon: Calendar },
    { id: 'budget', label: 'Budget', icon: Banknote },
    { id: 'performance', label: 'Performance', icon: TrendingUp },
    { id: 'detailed', label: 'Detailed', icon: FileText }
  ]

  const { data: projects, isLoading } = useQuery(
    ['projects-reports'],
    () => projectService.getProjects({ limit: 100 }),
    { select: (r) => r?.data?.data?.projects || [] }
  )

  const filteredProjects = useMemo(() => {
    if (!projects) return []
    return projects.filter(p => {
      if (statusFilter !== 'all' && p.status !== statusFilter) return false
      // Filter by project date range overlap
      if (dateFrom && p.end_date && new Date(p.end_date) < new Date(dateFrom)) return false
      if (dateTo && p.start_date && new Date(p.start_date) > new Date(dateTo)) return false
      return true
    })
  }, [projects, statusFilter, dateFrom, dateTo])

  const stats = useMemo(() => {
    const fp = filteredProjects
    return {
      total: fp.length,
      active: fp.filter(p => p.status === 'active').length,
      completed: fp.filter(p => p.status === 'completed').length,
      onHold: fp.filter(p => p.status === 'on_hold').length,
      planning: fp.filter(p => p.status === 'planning').length,
      totalBudget: fp.reduce((s, p) => s + (parseFloat(p.budget_allocated) || 0), 0),
      spentBudget: fp.reduce((s, p) => s + (parseFloat(p.budget_spent) || 0), 0),
      avgProgress: fp.length > 0 ? Math.round(fp.reduce((s, p) => s + (p.progress_percentage || 0), 0) / fp.length) : 0
    }
  }, [filteredProjects])


  const exportToExcel = () => {
    const wb = XLSX.utils.book_new()
    const filename = `project_${activeTab}_${new Date().toISOString().split('T')[0]}.xlsx`
    
    if (activeTab === 'overview') {
      const summaryData = [
        { 'Metric': 'Total Projects', 'Value': stats.total },
        { 'Metric': 'Active', 'Value': stats.active },
        { 'Metric': 'Completed', 'Value': stats.completed },
        { 'Metric': 'On Hold', 'Value': stats.onHold },
        { 'Metric': 'Planning', 'Value': stats.planning },
        { 'Metric': 'Total Budget', 'Value': stats.totalBudget },
        { 'Metric': 'Spent Budget', 'Value': stats.spentBudget },
        { 'Metric': 'Avg Progress', 'Value': `${stats.avgProgress}%` }
      ]
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summaryData), 'Overview')
    } else if (activeTab === 'gantt') {
      const ganttRows = []
      filteredProjects.forEach(p => {
        ganttRows.push({ 'Type': 'Project', 'Project': p.name, 'Code': p.code, 'Item': p.name, 'Start Date': p.start_date, 'End Date': p.end_date, 'Status': p.status, 'Progress': `${p.progress_percentage || 0}%` })
        ;(p.milestones || []).forEach(m => {
          ganttRows.push({ 'Type': 'Milestone', 'Project': p.name, 'Code': p.code, 'Item': m.name, 'Start Date': m.date, 'End Date': m.date, 'Status': m.status, 'Progress': m.completed ? '100%' : '' })
          ;(m.deliverables || []).forEach(d => {
            ganttRows.push({ 'Type': 'Deliverable', 'Project': p.name, 'Code': p.code, 'Item': d.name, 'Start Date': d.due_date, 'End Date': d.due_date, 'Status': d.status, 'Progress': d.completed ? '100%' : '' })
          })
        })
      })
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(ganttRows), 'Gantt Chart')
    } else if (activeTab === 'budget') {
      const budgetData = filteredProjects.map(p => ({
        'Code': p.code,
        'Project': p.name,
        'Status': p.status,
        'Budget Allocated': p.budget_allocated || 0,
        'Budget Spent': p.budget_spent || 0,
        'Remaining': (p.budget_allocated || 0) - (p.budget_spent || 0),
        'Utilization %': p.budget_allocated > 0 ? Math.round((p.budget_spent || 0) / p.budget_allocated * 100) : 0
      }))
      budgetData.push({ 'Code': '', 'Project': 'TOTAL', 'Status': '', 'Budget Allocated': stats.totalBudget, 'Budget Spent': stats.spentBudget, 'Remaining': stats.totalBudget - stats.spentBudget, 'Utilization %': stats.totalBudget > 0 ? Math.round(stats.spentBudget / stats.totalBudget * 100) : 0 })
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(budgetData), 'Budget')
    } else if (activeTab === 'performance') {
      const perfData = filteredProjects.map(p => ({
        'Code': p.code,
        'Project': p.name,
        'Status': p.status,
        'Progress %': p.progress_percentage || 0,
        'Start Date': p.start_date,
        'End Date': p.end_date,
        'Days Remaining': Math.max(0, Math.ceil((new Date(p.end_date) - new Date()) / (1000 * 60 * 60 * 24))),
        'On Track': (p.progress_percentage || 0) >= 50 ? 'Yes' : 'No'
      }))
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(perfData), 'Performance')
    } else if (activeTab === 'detailed') {
      const detailedData = filteredProjects.map(p => ({
        'Code': p.code,
        'Name': p.name,
        'Description': p.description || '',
        'Status': p.status,
        'Priority': p.priority,
        'Start Date': p.start_date,
        'End Date': p.end_date,
        'Progress %': p.progress_percentage || 0,
        'Budget Allocated': p.budget_allocated || 0,
        'Budget Spent': p.budget_spent || 0,
        'Manager': p.Manager?.User ? `${p.Manager.User.first_name} ${p.Manager.User.last_name}` : '',
        'Department': p.Department?.name || '',
        'Client': p.client_name || ''
      }))
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(detailedData), 'Detailed')
    }
    
    XLSX.writeFile(wb, filename)
  }

  if (isLoading) return <div className="p-8"><div className="animate-pulse h-64 bg-gray-200 rounded-xl"></div></div>

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Project Reports</h1>
          <p className="text-gray-500">Analytics and insights for {currentYear}</p>
        </div>
        <button onClick={exportToExcel} className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
          <Download className="h-4 w-4" /> Export Excel
        </button>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border">
        <div className="border-b px-4">
          <nav className="flex gap-1">
            {tabs.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  activeTab === tab.id ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}>
                <tab.icon className="h-4 w-4" /> {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Filters */}
        <div className="p-4 bg-gray-50 border-b flex flex-wrap gap-4 items-center">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">From:</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="px-3 py-1.5 border rounded-lg text-sm" />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">To:</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="px-3 py-1.5 border rounded-lg text-sm" />
          </div>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-3 py-1.5 border rounded-lg text-sm">
            <option value="all">All Status</option>
            <option value="planning">Planning</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="on_hold">On Hold</option>
          </select>
          <button onClick={() => { setDateFrom(`${currentYear}-01-01`); setDateTo(`${currentYear}-12-31`); setStatusFilter('all') }}
            className="text-sm text-primary-600 hover:underline">Reset</button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'Total Projects', value: stats.total, icon: Target, color: 'blue' },
                  { label: 'Active', value: stats.active, icon: Clock, color: 'green' },
                  { label: 'Completed', value: stats.completed, icon: CheckCircle, color: 'emerald' },
                  { label: 'Avg Progress', value: `${stats.avgProgress}%`, icon: TrendingUp, color: 'purple' }
                ].map((item, i) => (
                  <div key={i} className="bg-gradient-to-br from-gray-50 to-white border rounded-xl p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">{item.label}</p>
                        <p className="text-2xl font-bold text-gray-900 mt-1">{item.value}</p>
                      </div>
                      <div className={`p-3 rounded-xl bg-${item.color}-100`}>
                        <item.icon className={`h-6 w-6 text-${item.color}-600`} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white border rounded-xl p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">Status Distribution</h3>
                  <div className="space-y-3">
                    {[
                      { label: 'Active', value: stats.active, color: 'bg-blue-500' },
                      { label: 'Completed', value: stats.completed, color: 'bg-green-500' },
                      { label: 'Planning', value: stats.planning, color: 'bg-gray-400' },
                      { label: 'On Hold', value: stats.onHold, color: 'bg-yellow-500' }
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${item.color}`}></div>
                        <span className="flex-1 text-sm text-gray-600">{item.label}</span>
                        <span className="font-semibold">{item.value}</span>
                        <div className="w-24 h-2 bg-gray-100 rounded-full">
                          <div className={`h-full rounded-full ${item.color}`} style={{ width: `${stats.total > 0 ? (item.value / stats.total) * 100 : 0}%` }}></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-white border rounded-xl p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">Budget Overview</h3>
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600">Total Allocated</span>
                        <span className="font-semibold">{formatCurrency(stats.totalBudget)}</span>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600">Total Spent</span>
                        <span className="font-semibold text-orange-600">{formatCurrency(stats.spentBudget)}</span>
                      </div>
                    </div>
                    <div className="pt-3 border-t">
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-gray-600">Utilization</span>
                        <span className="font-semibold">{stats.totalBudget > 0 ? Math.round((stats.spentBudget / stats.totalBudget) * 100) : 0}%</span>
                      </div>
                      <div className="h-3 bg-gray-100 rounded-full">
                        <div className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full" style={{ width: `${stats.totalBudget > 0 ? (stats.spentBudget / stats.totalBudget) * 100 : 0}%` }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Gantt Tab */}
          {activeTab === 'gantt' && (
            <ProjectGanttReport />
          )}

          {/* Budget Tab */}
          {activeTab === 'budget' && (
            <div className="space-y-6">
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white">
                  <p className="text-blue-100 text-sm">Total Budget</p>
                  <p className="text-3xl font-bold mt-1">{formatCurrency(stats.totalBudget)}</p>
                </div>
                <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white">
                  <p className="text-orange-100 text-sm">Total Spent</p>
                  <p className="text-3xl font-bold mt-1">{formatCurrency(stats.spentBudget)}</p>
                </div>
                <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white">
                  <p className="text-green-100 text-sm">Remaining</p>
                  <p className="text-3xl font-bold mt-1">{formatCurrency(stats.totalBudget - stats.spentBudget)}</p>
                </div>
              </div>
              <div className="border rounded-xl overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Project</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Allocated</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Spent</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Remaining</th>
                      <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">Utilization</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredProjects.map(p => {
                      const allocated = parseFloat(p.budget_allocated) || 0
                      const spent = parseFloat(p.budget_spent) || 0
                      const util = allocated > 0 ? Math.round((spent / allocated) * 100) : 0
                      return (
                        <tr key={p.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3"><div className="font-medium">{p.name}</div><div className="text-xs text-gray-500">{p.code}</div></td>
                          <td className="px-4 py-3 text-right">{formatCurrency(allocated)}</td>
                          <td className="px-4 py-3 text-right text-orange-600">{formatCurrency(spent)}</td>
                          <td className="px-4 py-3 text-right text-green-600">{formatCurrency(allocated - spent)}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2 justify-center">
                              <div className="w-16 h-2 bg-gray-100 rounded-full"><div className={`h-full rounded-full ${util > 90 ? 'bg-red-500' : util > 70 ? 'bg-yellow-500' : 'bg-green-500'}`} style={{ width: `${Math.min(util, 100)}%` }}></div></div>
                              <span className="text-sm">{util}%</span>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Performance Tab */}
          {activeTab === 'performance' && (
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="border rounded-xl p-6">
                  <h3 className="font-semibold mb-4">Project Progress</h3>
                  <div className="space-y-4">
                    {filteredProjects.slice(0, 8).map(p => (
                      <div key={p.id}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="truncate pr-4">{p.name}</span>
                          <span className="font-medium">{p.progress_percentage || 0}%</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full">
                          <div className={`h-full rounded-full ${(p.progress_percentage || 0) >= 80 ? 'bg-green-500' : (p.progress_percentage || 0) >= 50 ? 'bg-blue-500' : 'bg-yellow-500'}`} style={{ width: `${p.progress_percentage || 0}%` }}></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="border rounded-xl p-6">
                  <h3 className="font-semibold mb-4">Completion Rate</h3>
                  <div className="flex items-center justify-center h-48">
                    <div className="relative">
                      <svg className="w-40 h-40 transform -rotate-90">
                        <circle cx="80" cy="80" r="70" fill="none" stroke="#e5e7eb" strokeWidth="12" />
                        <circle cx="80" cy="80" r="70" fill="none" stroke="#10b981" strokeWidth="12" strokeDasharray={`${stats.total > 0 ? (stats.completed / stats.total) * 440 : 0} 440`} />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center">
                          <p className="text-3xl font-bold">{stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0}%</p>
                          <p className="text-sm text-gray-500">Completed</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Detailed Tab */}
          {activeTab === 'detailed' && (
            <div className="border rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Project</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Progress</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Manager</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Timeline</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Budget</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredProjects.map(p => (
                    <tr key={p.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3"><div className="font-medium">{p.name}</div><div className="text-xs text-gray-500">{p.code}</div></td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded-full ${p.status === 'completed' ? 'bg-green-100 text-green-700' : p.status === 'active' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>{p.status}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-20 h-2 bg-gray-100 rounded-full"><div className="h-full bg-primary-500 rounded-full" style={{ width: `${p.progress_percentage || 0}%` }}></div></div>
                          <span className="text-sm">{p.progress_percentage || 0}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">{p.Manager?.User ? `${p.Manager.User.first_name} ${p.Manager.User.last_name}` : '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{new Date(p.start_date).toLocaleDateString()} - {new Date(p.end_date).toLocaleDateString()}</td>
                      <td className="px-4 py-3 text-right font-medium">{formatCurrency(p.budget_allocated)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ProjectReports
